import { useEffect, useRef, useState } from 'react';
import type { ParticipantId, RoomId, RoundId } from '@zrc/core';
import type { ControllerSession } from '../controller.js';
import { Status, fmtTime } from './common.js';

export function LiveView({ session }: { session: ControllerSession }) {
  const eng = session.engine;
  const rec = eng?.record;
  // One transition id per operator intent; repeated clicks reuse it (double-click test).
  const txRef = useRef<string | null>(null);
  const [, force] = useState(0);
  const [autoOpen, setAutoOpen] = useState(false);
  const [extraMinutes, setExtraMinutes] = useState(0);
  const [paused, setPaused] = useState(false);
  const [pausedAt, setPausedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!eng || !rec?.scheduleVersion) return <p className="muted">Import a schedule in Setup first.</p>;
  if (session.needsReconcileAfterReload) return <div className="banner banner-warn">Controller reloaded: go to Check-in and read live attendees before issuing any command.</div>;

  const schedule = rec.scheduleVersion.schedule;
  const rounds = [...schedule.rounds].sort((a, b) => a.sequence - b.sequence);
  const current = rounds.find((r) => r.roundId === rec.currentRoundId) ?? null;
  const active = rec.activeTransition;
  const txId = active?.transitionId ?? txRef.current;
  const plan = active && active.targetRoundId === null ? null : eng.previewNext();
  const names = new Map(schedule.participants.map((p) => [p.participantId, p.displayName]));
  const roomNames = new Map(schedule.rooms.map((r) => [r.roomId, r.roomName]));
  const roomName = (id: RoomId) => (id === 'MAIN' ? 'Main session' : roomNames.get(id) ?? id);
  const canAct = session.hasControl && !session.needsReconcileAfterReload;

  const mint = () => {
    if (!txRef.current) txRef.current = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `tx-${Date.now()}`;
    return txRef.current;
  };
  const done = () => {
    txRef.current = null;
    force((n) => n + 1);
  };

  // Advisory timer (FR10).
  const openedAt = current ? Date.parse(rec.roundTimes[current.roundId]?.openedAt ?? '') : NaN;
  const budgetMs = current ? (current.durationMinutes + extraMinutes) * 60_000 : 0;
  const elapsed = Number.isFinite(openedAt) ? (paused && pausedAt ? pausedAt : now) - openedAt : 0;
  const remaining = Math.max(0, budgetMs - elapsed);
  const mmss = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;

  const startSequence = async () => {
    const id = mint();
    await session.begin(id, 'next');
    const t = eng.record.activeTransition;
    if (!t || t.transitionId !== id) return;
    if (t.phase === 'closing') await session.closeRooms(id);
    const t2 = eng.record.activeTransition;
    if (t2?.phase === 'checkpoint') await session.checkpoint(id);
    if (autoOpen) await continueAfterCheckpoint();
  };
  const continueAfterCheckpoint = async () => {
    const id = txId ?? mint();
    const t = eng.record.activeTransition;
    if (t?.phase === 'writing') await session.write(id);
    if (eng.record.activeTransition?.phase === 'opening') {
      await session.open(id);
      done();
    }
  };

  return (
    <div>
      <div className="row">
        <div>
          <div className="muted">State</div>
          <strong>{rec.state.replace('_', ' ')}</strong>
        </div>
        <div>
          <div className="muted">Current round</div>
          <strong>{current ? `${current.roundId} (${current.sequence} of ${rounds.length})` : 'none'}</strong>
        </div>
        {current && rec.state === 'round_active' && (
          <div>
            <div className="muted">Remaining (advisory)</div>
            <div className="timer" aria-live="polite">
              {mmss(remaining)}
            </div>
            <div className="row">
              <button className="secondary" onClick={() => { setPaused((p) => !p); setPausedAt(paused ? null : Date.now()); }}>
                {paused ? 'Resume timer' : 'Pause timer'}
              </button>
              <button className="secondary" onClick={() => setExtraMinutes((m) => m + 2)}>
                +2 min
              </button>
            </div>
          </div>
        )}
      </div>

      {rec.state === 'needs_attention' && active && (
        <div className="banner banner-error" role="alert">
          <strong>Needs attention.</strong> {active.failed.length} failed write(s), {active.mismatched.length} listing mismatch(es).
          <ul>
            {active.failed.map((f) => (
              <li key={f.participantId}>
                {names.get(f.participantId)}: {f.error}
              </li>
            ))}
            {active.mismatched.map((m) => (
              <li key={m.participantId}>
                {names.get(m.participantId)}: intended {roomName(m.intended)}, listed {m.listed === 'unlisted' ? 'nowhere' : roomName(m.listed)}
              </li>
            ))}
          </ul>
          <div className="row">
            <button className="primary" disabled={!canAct || active.phase === 'failed'} onClick={() => void session.retry(active.transitionId)}>
              Retry unresolved
            </button>
            <button className="secondary" disabled={!canAct || active.phase === 'failed'} onClick={() => void session.acceptExceptions(active.transitionId)}>
              Open with these exceptions
            </button>
          </div>
          {active.phase === 'failed' && <p>The close or open command itself failed. Check Zoom's breakout panel, then use Zoom's own controls to finish this round if the SDK is unavailable: Close all rooms, wait for everyone to return, assign by hand from the preview below, Open all rooms.</p>}
        </div>
      )}

      {plan && rec.state !== 'completed' && (
        <>
          <h2>Next: {plan.roundId}</h2>
          <p className="muted">
            {plan.changes.length} move(s), {plan.unchanged.length} unchanged, {plan.absent.length} absent, {plan.excluded.length} excluded.
          </p>
          {plan.blockedRooms.length > 0 && (
            <div className="banner banner-warn">
              Rooms without a present specialist: {plan.blockedRooms.map((b) => `${roomName(b.roomId)} (${b.assignedStudents.length} students)`).join(', ')}. Override or mark the specialist present before opening.
            </div>
          )}
          {plan.overCapacity.length > 0 && (
            <div className="banner banner-error">Over capacity: {plan.overCapacity.map((o) => `${roomName(o.roomId)} ${o.count}/${o.capacity}`).join(', ')}.</div>
          )}
          <table>
            <thead>
              <tr>
                <th>Who</th>
                <th>From</th>
                <th>To</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {plan.changes.map((c) => (
                <tr key={c.participantId}>
                  <td>
                    {names.get(c.participantId)} <span className="muted">{c.role}</span>
                  </td>
                  <td>{roomName(c.from)}</td>
                  <td>{roomName(c.to)}</td>
                  <td>{c.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h2>Controls</h2>
      <div className="row">
        <label>
          <input type="checkbox" checked={autoOpen} onChange={(e) => setAutoOpen(e.target.checked)} /> Open rooms automatically after the checkpoint if nothing needs attention
        </label>
      </div>
      <div className="row">
        {!active && (rec.state === 'ready' || rec.state === 'round_active') && (
          <button className="primary" disabled={!canAct || !plan || plan.overCapacity.length > 0} onClick={() => void startSequence()}>
            {rec.state === 'ready' ? 'Start first round' : `Close rooms and prepare ${plan?.roundId ?? ''}`}
          </button>
        )}
        {active && active.phase === 'closing' && (
          <button className="primary" disabled={!canAct} onClick={() => void session.closeRooms(active.transitionId)}>
            Close rooms (retry)
          </button>
        )}
        {active && active.phase === 'checkpoint' && (
          <button className="primary" disabled={!canAct} onClick={() => void session.checkpoint(active.transitionId)}>
            Read attendees at checkpoint
          </button>
        )}
        {active && (active.phase === 'writing' || active.phase === 'opening') && rec.state !== 'needs_attention' && (
          <button className="primary" disabled={!canAct || (plan?.blockedRooms.length ?? 0) > 0} onClick={() => void continueAfterCheckpoint()}>
            {active.phase === 'writing' ? 'Write assignments and open rooms' : 'Open rooms'}
          </button>
        )}
        {!active && rec.state === 'round_active' && (
          <button className="secondary" disabled={!canAct} onClick={() => void session.endRotations(mint()).then(done)}>
            End rotations
          </button>
        )}
        {rec.state === 'round_active' && (
          <button className="secondary" disabled={!canAct} onClick={() => void session.detectDiscrepancies()}>
            Check rooms against plan
          </button>
        )}
      </div>
      {active && (
        <p className="muted">
          Transition {active.transitionId.slice(0, 8)} · phase {active.phase} · started {fmtTime(active.startedAt)}
        </p>
      )}
      {session.discrepancies.length > 0 && (
        <div className="banner banner-warn">
          Zoom differs from the plan (not changed automatically):
          <ul>
            {session.discrepancies.map((d) => (
              <li key={d.participantId}>
                {names.get(d.participantId)}: planned {roomName(d.intended)}, Zoom shows {d.listed === 'unlisted' ? 'nowhere' : roomName(d.listed)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {current && (
        <>
          <h2>Round {current.roundId} status</h2>
          <table>
            <thead>
              <tr>
                <th>Who</th>
                <th>Planned room</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {schedule.participants.map((p) => {
                const s = rec.statuses[current.roundId]?.[p.participantId];
                const dest = schedule.assignments.find((a) => a.roundId === current.roundId && a.participantId === p.participantId)?.destinationRoomId;
                return (
                  <tr key={p.participantId}>
                    <td>{p.displayName}</td>
                    <td>{dest ? roomName(dest) : ''}</td>
                    <td>{s ? <Status s={s} /> : <span className="muted">no record</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      <OverrideForm session={session} />
    </div>
  );
}

function OverrideForm({ session }: { session: ControllerSession }) {
  const rec = session.engine!.record;
  const schedule = rec.scheduleVersion!.schedule;
  const rounds = [...schedule.rounds].sort((a, b) => a.sequence - b.sequence);
  const [pid, setPid] = useState<string>(schedule.participants[0]?.participantId ?? '');
  const [kind, setKind] = useState<'hold_in_main' | 'exclude' | 'redirect'>('hold_in_main');
  const [room, setRoom] = useState<string>(schedule.rooms[0]?.roomId ?? '');
  const [scope, setScope] = useState<'current_round' | 'remaining_rounds'>('current_round');
  const fromRound = (rec.state === 'round_active' ? rounds[rounds.findIndex((r) => r.roundId === rec.currentRoundId) + 1] : rounds.find((r) => r.roundId === rec.currentRoundId) ?? rounds[0]) ?? rounds[0];
  if (!fromRound) return null;
  return (
    <div>
      <h2>Override (applies from {fromRound.roundId})</h2>
      <div className="row">
        <select aria-label="Participant" value={pid} onChange={(e) => setPid(e.target.value)}>
          {schedule.participants.map((p) => (
            <option key={p.participantId} value={p.participantId}>
              {p.displayName}
            </option>
          ))}
        </select>
        <select aria-label="Action" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          <option value="hold_in_main">Hold in main session</option>
          <option value="redirect">Send to room</option>
          <option value="exclude">Exclude</option>
        </select>
        {kind === 'redirect' && (
          <select aria-label="Room" value={room} onChange={(e) => setRoom(e.target.value)}>
            {schedule.rooms.map((r) => (
              <option key={r.roomId} value={r.roomId}>
                {r.roomName}
              </option>
            ))}
          </select>
        )}
        <select aria-label="Scope" value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
          <option value="current_round">This round only</option>
          <option value="remaining_rounds">All remaining rounds</option>
        </select>
        <button
          className="secondary"
          disabled={!session.hasControl}
          onClick={() =>
            void session.addOverride({
              participantId: pid as ParticipantId,
              scope,
              fromRoundId: fromRound.roundId as RoundId,
              action: kind === 'redirect' ? { kind, destinationRoomId: room as RoomId } : { kind },
            })
          }
        >
          Add override
        </button>
      </div>
    </div>
  );
}
